import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Lock, ArrowLeft, RefreshCw, MessageSquareText, Bug, Lightbulb,
  MessageCircle, CheckCircle2, Trash2, RotateCcw, Smartphone, Monitor,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminFeedbackList, adminFeedbackStatus, adminFeedbackDelete } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHu = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const CAT = {
  bug:   { label: 'Hiba',  icon: Bug,           cls: 'bg-red-500/15 text-red-300' },
  idea:  { label: 'Ötlet', icon: Lightbulb,     cls: 'bg-amber-500/15 text-amber-300' },
  other: { label: 'Egyéb', icon: MessageCircle, cls: 'bg-sky-500/15 text-sky-300' },
};

const FeedbackCard = ({ f, onStatus, onDelete }) => {
  const cat = CAT[f.category] || CAT.other;
  const Icon = cat.icon;
  const resolved = f.status === 'resolved';
  return (
    <div data-testid={`feedback-card-${f.id}`}
      className={`rounded-xl border p-4 md:p-5 ${resolved ? 'border-gray-800 bg-gray-900/30 opacity-70' : 'border-gray-800 bg-gray-900/60'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${cat.cls}`}>
            <Icon size={10} /> {cat.label}
          </span>
          {f.device?.is_mobile !== undefined && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-gray-700/40 text-gray-300">
              {f.device.is_mobile ? <Smartphone size={10} /> : <Monitor size={10} />}
              {f.device.is_mobile ? 'Mobil' : 'Asztali'} {f.device.viewport ? `· ${f.device.viewport}` : ''}
            </span>
          )}
          {resolved && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">
              <CheckCircle2 size={10} /> Elintézve
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">{fmtHu(f.created_at)}</span>
      </div>
      <p className="text-sm text-gray-200 whitespace-pre-wrap mb-3">{f.message}</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-500 min-w-0">
          {f.name || '—'} · <span className="font-mono">{f.email}</span>
          {f.page ? ` · ${f.page}` : ''}
          {f.device?.ua ? <span className="block truncate max-w-md mt-0.5 text-gray-600">{f.device.ua}</span> : null}
        </div>
        <div className="flex gap-2">
          <Button data-testid={`feedback-toggle-${f.id}`} variant="outline" size="sm"
            onClick={() => onStatus(f, resolved ? 'new' : 'resolved')}
            className={resolved
              ? 'border-gray-700 text-gray-400 hover:bg-gray-800'
              : 'border-emerald-800/60 text-emerald-400 hover:bg-emerald-500/10'}>
            {resolved ? <><RotateCcw size={13} className="mr-1.5" /> Újranyitás</> : <><CheckCircle2 size={13} className="mr-1.5" /> Elintézve</>}
          </Button>
          <Button data-testid={`feedback-delete-${f.id}`} variant="outline" size="sm" onClick={() => onDelete(f)}
            className="border-red-900/60 text-red-400 hover:bg-red-500/10">
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminFeedbackPage() {
  useDocumentMeta({ title: 'Visszajelzések — Admin | Orosz Coaching', noindex: true });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      setData(await adminFeedbackList(key));
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Érvénytelen admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else toast.error('Betöltési hiba.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey, load]);

  const setStatus = async (f, status) => {
    try {
      await adminFeedbackStatus(adminKey, f.id, status);
      load(adminKey);
    } catch (_) { toast.error('Nem sikerült frissíteni.'); }
  };

  const remove = async (f) => {
    try {
      await adminFeedbackDelete(adminKey, f.id);
      toast.success('Törölve.');
      load(adminKey);
    } catch (_) { toast.error('A törlés nem sikerült.'); }
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <form data-testid="admin-feedback-login"
          onSubmit={(e) => { e.preventDefault(); if (keyInput.trim()) { localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim()); setAdminKey(keyInput.trim()); } }}
          className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <MessageSquareText className="text-yellow-500" size={28} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Visszajelzések</h1>
          <Input data-testid="admin-feedback-key" type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
            placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
          <Button data-testid="admin-feedback-login-btn" type="submit" disabled={!keyInput.trim()}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
            <Lock className="mr-2" size={16} /> Belépés
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-feedback-back" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <MessageSquareText className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Visszajelzések</h1>
              <p className="text-xs text-gray-400">
                Tesztelői és ügyfél visszajelzések a portálról
                {data ? ` — ${data.total} összesen, ${data.new_count} új` : ''}
              </p>
            </div>
          </div>
          <Button data-testid="admin-feedback-refresh" variant="ghost" size="sm" onClick={() => load(adminKey)}
            className="text-gray-400 hover:text-white">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-yellow-500" size={28} /></div>
        ) : (
          <div data-testid="admin-feedback-list" className="space-y-3">
            {(data?.items || []).map((f) => <FeedbackCard key={f.id} f={f} onStatus={setStatus} onDelete={remove} />)}
            {(data?.items || []).length === 0 && (
              <p className="text-center text-gray-500 py-10">Még nincs visszajelzés.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
